import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ChevronRight, Search } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';
import {
  ADMIN_NAV,
  adminNavHref,
  type AdminNavItem,
  type AdminNavSection,
} from '@/config/adminNav';
import { AdminEventSwitcher } from '@/components/admin/AdminEventSwitcher';
import type { AdminMode } from '@/components/admin/AdminModeToggle';

const DEFAULT_MODE: AdminMode = 'organization';

/** First tab of each mode = that mode panel's default tab (matches the panels' fallbacks). */
const DEFAULT_TAB_BY_MODE: Partial<Record<AdminMode, string>> = (() => {
  const map: Partial<Record<AdminMode, string>> = {};
  for (const section of ADMIN_NAV) {
    for (const item of section.items) {
      if ('mode' in item.target && item.target.tab && map[item.target.mode] === undefined) {
        map[item.target.mode] = item.target.tab;
      }
    }
  }
  return map;
})();

interface AdminSidebarProps {
  stats: {
    pendingApplications: number;
    pendingEventDrafts: number;
    pendingOrders: number;
    unreadMessages: number;
    openLeads: number;
    activeAffiliates?: number;
  };
}

export function AdminSidebar({ stats }: AdminSidebarProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { setOpenMobile, isMobile } = useSidebar();

  const currentMode = (searchParams.get('mode') as AdminMode) || DEFAULT_MODE;
  const currentTab = searchParams.get('tab');
  const onDashboard = location.pathname === '/admin';

  const isItemActive = (item: AdminNavItem): boolean => {
    if ('route' in item.target) {
      return location.pathname === item.target.route;
    }
    if (!onDashboard || currentMode !== item.target.mode) return false;
    if (!item.target.tab) return true;
    const effectiveTab = currentTab ?? DEFAULT_TAB_BY_MODE[item.target.mode];
    return effectiveTab === item.target.tab;
  };

  const isSectionActive = (section: AdminNavSection): boolean =>
    section.items.some(isItemActive);

  // Current location as a breadcrumb ("Section › Item"), for "where am I".
  let activeCrumb: { section: string; item: string } | null = null;
  for (const section of ADMIN_NAV) {
    const match = section.items.find(isItemActive);
    if (match) {
      activeCrumb = { section: section.label, item: match.label };
      break;
    }
  }

  const badgeValue = (item: AdminNavItem): number | null => {
    if (item.badge === 'pendingApplications') return stats.pendingApplications || null;
    if (item.badge === 'pendingEventDrafts') return stats.pendingEventDrafts || null;
    if (item.badge === 'pendingOrders') return stats.pendingOrders || null;
    if (item.badge === 'unreadMessages') return stats.unreadMessages || null;
    if (item.badge === 'openLeads') return stats.openLeads || null;
    if (item.badge === 'activeAffiliates') return stats.activeAffiliates || null;
    return null;
  };

  const handleNavigate = (item: AdminNavItem) => {
    if ('mode' in item.target) {
      localStorage.setItem('admin_last_mode', item.target.mode);
    }
    navigate(adminNavHref(item.target));
    if (isMobile) setOpenMobile(false);
  };

  // Simple filter: type to narrow the whole menu by label/keywords.
  const [filter, setFilter] = useState('');
  const query = filter.trim().toLowerCase();
  const filtering = query.length > 0;
  const matchesQuery = (item: AdminNavItem) =>
    !filtering ||
    item.label.toLowerCase().includes(query) ||
    item.keywords.some(k => k.toLowerCase().includes(query));

  const visibleSections = ADMIN_NAV
    .map(section => ({ section, items: section.items.filter(matchesQuery) }))
    .filter(({ items }) => items.length > 0);

  return (
    // Offset below the global floating pill navigation (fixed, ~88px tall).
    <Sidebar className="!top-[88px] !h-[calc(100svh-88px)]">
      <SidebarHeader className="px-4 py-3 gap-2">
        <span className="text-sm font-semibold tracking-tight">Admin</span>
        {activeCrumb && (
          <span className="text-xs text-muted-foreground truncate">
            {activeCrumb.section} › {activeCrumb.item}
          </span>
        )}
        {/* Global event context — everything event-scoped in the admin follows this selection. */}
        <div className="mt-1" data-tour="event-switcher">
          <AdminEventSwitcher compact />
        </div>
        <div className="relative mt-1">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <SidebarInput
            value={filter}
            onChange={e => setFilter(e.target.value)}
            placeholder="Filter menu…"
            className="pl-7"
          />
        </div>
      </SidebarHeader>
      <SidebarContent>
        {visibleSections.length === 0 && (
          <p className="px-4 py-3 text-xs text-muted-foreground">No matches.</p>
        )}
        {visibleSections.map(({ section, items }) => {
          const sectionActive = isSectionActive(section);
          return (
            <Collapsible
              key={`${section.id}-${filtering}`}
              defaultOpen={sectionActive || filtering}
              className="group/collapsible"
            >
              <SidebarGroup>
                <SidebarGroupLabel asChild>
                  <CollapsibleTrigger className="flex w-full items-center gap-2">
                    <section.icon className="h-4 w-4" />
                    <span className="flex-1 text-left">{section.label}</span>
                    <ChevronRight className="h-4 w-4 transition-transform group-data-[state=open]/collapsible:rotate-90" />
                  </CollapsibleTrigger>
                </SidebarGroupLabel>
                <CollapsibleContent>
                  <SidebarGroupContent>
                    <SidebarMenu>
                      {items.map((item) => {
                        const badge = badgeValue(item);
                        return (
                          <SidebarMenuItem key={item.id}>
                            <SidebarMenuButton
                              isActive={isItemActive(item)}
                              onClick={() => handleNavigate(item)}
                              tooltip={item.label}
                            >
                              <item.icon className="h-4 w-4" />
                              <span>{item.label}</span>
                            </SidebarMenuButton>
                            {badge !== null && (
                              <SidebarMenuBadge>{badge}</SidebarMenuBadge>
                            )}
                          </SidebarMenuItem>
                        );
                      })}
                    </SidebarMenu>
                  </SidebarGroupContent>
                </CollapsibleContent>
              </SidebarGroup>
            </Collapsible>
          );
        })}
      </SidebarContent>
    </Sidebar>
  );
}
